// Toast composable
export interface ToastOptions {
  message: string
  variant?: 'info' | 'success' | 'error' | 'warning' | 'primary'
  appearance?: 'solid' | 'outline' | 'light'
  size?: 'sm' | 'md' | 'lg'
  duration?: number
  position?: 'top-end' | 'top-center' | 'top-start' | 'bottom-end' | 'bottom-center' | 'bottom-start'
  icon?: string | boolean
  progress?: boolean
  important?: boolean
  dismiss?: boolean
}

// Classe global para gerenciar toasts
class ToastManager {
  private container: HTMLElement | null = null
  private toasts: Map<string, HTMLElement> = new Map()
  private maxToasts = 5

  init() {
    this.container = document.getElementById('toast-container')
    if (!this.container) {
      this.container = document.createElement('div')
      this.container.id = 'toast-container'
      this.container.className = 'fixed inset-0 pointer-events-none'
      this.container.style.cssText = 'z-index: 10000; position: fixed; inset: 0; pointer-events: none;'
      document.body.appendChild(this.container)
    }
  }

  show(options: ToastOptions) {
    if (!this.container) this.init()

    const id = this.generateId()
    const toastElement = this.createToastElement(id, options)
    
    this.toasts.set(id, toastElement)
    this.addToContainer(toastElement, options.position || 'top-end')
    
    // Auto dismiss
    if (!options.important && options.duration !== 0) {
      const duration = options.duration || 4000
      setTimeout(() => {
        this.hide(id)
      }, duration)
    }

    return { id, element: toastElement, dismiss: () => this.hide(id) }
  }

  hide(id: string) {
    const toastElement = this.toasts.get(id)
    if (toastElement) {
      // Animate out
      toastElement.style.opacity = '0'
      toastElement.style.transform = 'translateX(100%)'
      
      setTimeout(() => {
        toastElement.remove()
        this.toasts.delete(id)
        this.updatePositions()
      }, 300)
    }
  }

  clearAll() {
    this.toasts.forEach((_, id) => this.hide(id))
  }

  private createToastElement(id: string, options: ToastOptions): HTMLElement {
    const toast = document.createElement('div')
    toast.id = id
    toast.className = this.getToastClasses(options)
    toast.style.cssText = `
      pointer-events: auto;
      transition: all 0.3s ease;
      opacity: 0;
      transform: translateX(100%);
      z-index: 10000;
      position: relative;
    `

    // Icon
    let iconHtml = ''
    if (options.icon !== false) {
      const iconClass = this.getIconClass(options.variant || 'info')
      iconHtml = `
        <div class="flex items-center justify-center size-8 rounded-lg flex-shrink-0 ${this.getIconBgClass(options.variant || 'info')}">
          <i class="${iconClass} text-sm"></i>
        </div>
      `
    }

    // Progress bar
    let progressHtml = ''
    if (options.progress && !options.important) {
      const duration = options.duration || 4000
      progressHtml = `
        <div class="absolute bottom-0 left-0 right-0 h-1 bg-black/10 rounded-b-lg overflow-hidden">
          <div class="h-full bg-current" style="animation: toast-progress ${duration}ms linear forwards;"></div>
        </div>
      `
    }

    toast.innerHTML = `
      <div class="flex items-start gap-3 p-4 relative" style="z-index: 10000;">
        ${iconHtml}
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-foreground break-words">
            ${options.message}
          </div>
        </div>
        ${options.dismiss !== false ? `
          <button type="button" class="flex-shrink-0 p-1 hover:bg-black/5 rounded transition-colors" data-toast-dismiss="${id}">
            <i class="ki-filled ki-cross text-xs text-muted-foreground"></i>
          </button>
        ` : ''}
      </div>
      ${progressHtml}
    `

    // Add dismiss event
    if (options.dismiss !== false) {
      const dismissBtn = toast.querySelector(`[data-toast-dismiss="${id}"]`)
      if (dismissBtn) {
        dismissBtn.addEventListener('click', () => this.hide(id))
      }
    }

    // Add progress animation style
    if (options.progress && !document.getElementById('toast-progress-style')) {
      const style = document.createElement('style')
      style.id = 'toast-progress-style'
      style.textContent = `
        @keyframes toast-progress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `
      document.head.appendChild(style)
    }

    return toast
  }

  private getToastClasses(options: ToastOptions): string {
    const variant = options.variant || 'info'
    const appearance = options.appearance || 'solid'
    const size = options.size || 'md'
    
    let classes = 'kt-toast rounded-lg shadow-lg border max-w-sm w-full relative overflow-hidden'
    
    // Size classes
    switch (size) {
      case 'sm':
        classes += ' text-xs'
        break
      case 'lg':
        classes += ' text-base'
        break
      default:
        classes += ' text-sm'
    }

    // Variant and appearance classes
    switch (appearance) {
      case 'outline':
        classes += ` bg-card border-2 ${this.getBorderClass(variant)}`
        break
      case 'light':
        classes += ` border-border ${this.getLightBgClass(variant)}`
        break
      default: // solid
        classes += ` border-transparent ${this.getSolidBgClass(variant)} text-white`
    }

    return classes
  }

  private getSolidBgClass(variant: string): string {
    switch (variant) {
      case 'success': return 'bg-green-600'
      case 'error': return 'bg-red-600'
      case 'warning': return 'bg-yellow-600'
      case 'primary': return 'bg-blue-600'
      default: return 'bg-blue-500'
    }
  }

  private getLightBgClass(variant: string): string {
    switch (variant) {
      case 'success': return 'bg-green-50'
      case 'error': return 'bg-red-50'
      case 'warning': return 'bg-yellow-50'
      case 'primary': return 'bg-blue-50'
      default: return 'bg-blue-50'
    }
  }

  private getBorderClass(variant: string): string {
    switch (variant) {
      case 'success': return 'border-green-500'
      case 'error': return 'border-red-500'
      case 'warning': return 'border-yellow-500'
      case 'primary': return 'border-blue-500'
      default: return 'border-blue-400'
    }
  }

  private getIconClass(variant: string): string {
    switch (variant) {
      case 'success': return 'ki-filled ki-check-circle text-green-600'
      case 'error': return 'ki-filled ki-cross-circle text-red-600'
      case 'warning': return 'ki-filled ki-information-2 text-yellow-600'
      case 'primary': return 'ki-filled ki-star text-blue-600'
      default: return 'ki-filled ki-information-1 text-blue-500'
    }
  }

  private getIconBgClass(variant: string): string {
    switch (variant) {
      case 'success': return 'bg-green-100'
      case 'error': return 'bg-red-100'
      case 'warning': return 'bg-yellow-100'
      case 'primary': return 'bg-blue-100'
      default: return 'bg-blue-100'
    }
  }

  private addToContainer(toastElement: HTMLElement, position: string) {
    if (!this.container) return

    let positionContainer = this.container.querySelector(`[data-position="${position}"]`) as HTMLElement
    
    if (!positionContainer) {
      positionContainer = document.createElement('div')
      positionContainer.setAttribute('data-position', position)
      positionContainer.className = this.getPositionClasses(position)
      this.container.appendChild(positionContainer)
    }

    // Remove excess toasts
    const existingToasts = positionContainer.children
    while (existingToasts.length >= this.maxToasts) {
      const oldestToast = existingToasts[0] as HTMLElement
      const oldestId = oldestToast.id
      this.hide(oldestId)
    }

    positionContainer.appendChild(toastElement)

    // Trigger animation
    requestAnimationFrame(() => {
      toastElement.style.opacity = '1'
      toastElement.style.transform = 'translateX(0)'
    })

    this.updatePositions()
  }

  private getPositionClasses(position: string): string {
    let classes = 'absolute flex flex-col gap-2 pointer-events-none'
    
    switch (position) {
      case 'top-start':
        return `${classes} top-4 left-4`
      case 'top-center':
        return `${classes} top-4 left-1/2 transform -translate-x-1/2`
      case 'top-end':
        return `${classes} top-4 right-4`
      case 'bottom-start':
        return `${classes} bottom-4 left-4`
      case 'bottom-center':
        return `${classes} bottom-4 left-1/2 transform -translate-x-1/2`
      case 'bottom-end':
        return `${classes} bottom-4 right-4`
      default:
        return `${classes} top-4 right-4`
    }
  }

  private updatePositions() {
    // Implementation for updating toast positions when one is removed
    // This is handled automatically by CSS flexbox in our implementation
  }

  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }
}

// Global toast instance
const toastManager = new ToastManager()

// Export toast functions
export const useToast = () => {
  return {
    show: (options: ToastOptions) => toastManager.show(options),
    hide: (id: string) => toastManager.hide(id),
    clearAll: () => toastManager.clearAll(),
    success: (message: string, options: Partial<ToastOptions> = {}) => 
      toastManager.show({ ...options, message, variant: 'success' }),
    error: (message: string, options: Partial<ToastOptions> = {}) => 
      toastManager.show({ ...options, message, variant: 'error' }),
    warning: (message: string, options: Partial<ToastOptions> = {}) => 
      toastManager.show({ ...options, message, variant: 'warning' }),
    info: (message: string, options: Partial<ToastOptions> = {}) => 
      toastManager.show({ ...options, message, variant: 'info' })
  }
}

// Initialize global KTToast for compatibility (if needed)
if (typeof window !== 'undefined') {
  (window as any).KTToast = {
    show: (options: ToastOptions) => toastManager.show(options),
    hide: (id: string) => toastManager.hide(id),
    clearAll: () => toastManager.clearAll()
  }
}